import { useState, useEffect, useRef, useCallback } from 'react';

const MediaPermission = () => {
  // const [permissionStatus, setPermissionStatus] = useState({
  //   audio: 'unknown',
  //   video: 'unknown'
  // });
  const [audioContext, setAudioContext] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const initTimeoutRef = useRef(null);
  const userInteractionHandled = useRef(false);

  const checkPermissions = useCallback(async () => {
    if (isInitialized || isChecking) return;
    
    try {
      setIsChecking(true);
      
      // Check existing permissions using Permissions API first
      let audioStatus = 'unknown';
      let videoStatus = 'unknown';
      
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const [audioPermission, videoPermission] = await Promise.all([
            navigator.permissions.query({ name: 'microphone' }).catch(() => null),
            navigator.permissions.query({ name: 'camera' }).catch(() => null)
          ]);

          audioStatus = audioPermission?.state === 'granted' ? 'granted' : 
                       audioPermission?.state === 'denied' ? 'denied' : 'unknown';
          videoStatus = videoPermission?.state === 'granted' ? 'granted' : 
                       videoPermission?.state === 'denied' ? 'denied' : 'unknown';

          // setPermissionStatus({
          //   audio: audioStatus,
          //   video: videoStatus
          // });

          // If permissions are already granted, we're done - no need to request again
          if (audioStatus === 'granted' && videoStatus === 'granted') {
            console.log('Permissions already granted');
            setIsInitialized(true);
            return;
          }
        } catch (err) {
          console.log('Permissions API query failed:', err);
        }
      }

      // Automatically request permissions if not already granted
      // This will show the browser permission dialog
      // We'll immediately stop tracks after getting permissions (no streams created)
      console.log('Requesting media permissions automatically...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        
        // Check what permissions were granted
        // const audioTracks = stream.getAudioTracks();
        // const videoTracks = stream.getVideoTracks();
        // const hasAudio = audioTracks.length > 0 && audioTracks[0].readyState === 'live';
        // const hasVideo = videoTracks.length > 0 && videoTracks[0].readyState === 'live';

        // Update permission status
        // setPermissionStatus({
        //   audio: hasAudio ? 'granted' : 'denied',
        //   video: hasVideo ? 'granted' : 'denied'
        // });

        // CRITICAL: Immediately stop all tracks - we only requested permissions, not streams
        // Streams will only be created when user clicks camera/audio buttons
        stream.getTracks().forEach(track => {
          track.stop();
        });
        
        console.log('Permissions requested. Tracks stopped. Streams will be created on user action.');
      } catch (err) {
        console.log('Permission request failed:', err.name, err.message);
        
        // // Update permission status based on error
        // if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        //   setPermissionStatus({
        //     audio: 'denied',
        //     video: 'denied'
        //   });
        // } else {
        //   setPermissionStatus({
        //     audio: 'unknown',
        //     video: 'unknown'
        //   });
        // }
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
    } finally {
      setIsChecking(false);
      setIsInitialized(true);
    }
  }, [isInitialized, isChecking]);

  const handleUserInteraction = useCallback(() => {
    if (userInteractionHandled.current) return;
    userInteractionHandled.current = true;

    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume()
        .then(() => console.log('AudioContext resumed by user interaction'))
        .catch(err => console.error('Failed to resume AudioContext:', err));
    }

    const globalAudioElement = document.getElementById('jssip-audio-element');
    if (globalAudioElement && globalAudioElement.srcObject) {
      globalAudioElement.play().catch(e => 
        console.warn('Play still failed after user interaction:', e)
      );
    }
  }, [audioContext]);

  useEffect(() => {
    // Initialize audio context
    if (!audioContext) {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const context = new AudioContextClass();
          setAudioContext(context);
          
          if (context.state === 'suspended') {
            console.log('AudioContext suspended, will need user interaction');
          }
        }
      } catch (err) {
        console.error('Failed to initialize AudioContext:', err);
      }
    }

    // Set initialization timeout
    initTimeoutRef.current = setTimeout(() => {
      if (!isInitialized) {
        console.warn('MediaPermission initialization timeout');
        setIsInitialized(true);
      }
    }, 1000);

    // Start permission checking after a short delay
    const permissionTimeout = setTimeout(() => {
      checkPermissions();
    }, 100);

    // Set up user interaction handlers
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    document.addEventListener('click', handleUserInteraction, { once: true });

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      if (permissionTimeout) {
        clearTimeout(permissionTimeout);
      }
      
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
      
      if (audioContext && audioContext.state !== 'closed') {
        try {
          audioContext.close();
        } catch (err) {
          console.error('Error closing AudioContext:', err);
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Component renders nothing - permissions are requested automatically without UI
  // Streams will only be created when user clicks camera/audio buttons
  return null;
};

export default MediaPermission;